"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  navigationCategories,
  NavigationLink,
  getContextualNavigationLinks,
  getCurrentSubdomainContext,
  getSubdomainLinks
} from "@/app/data/navigation";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Home,
  ChevronDown,
  MoreHorizontal,
  User,
  LogOut,
  Github,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/trpc/react";

import ThemeToggle from "./ThemeToggle";

// Animated Nav Link for the sliding indicator effect
export function AnimatedNavLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  const pathname = usePathname();
  const isActive = pathname === href;

  return (
    <Link
      href={href}
      className={cn(
        "relative flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors duration-200 hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:bg-accent/40",
        isActive ? "text-primary bg-accent/30" : "text-foreground/70 hover:text-foreground",
        className
      )}
    >
      <div className="flex items-center gap-1.5">
        {children}
      </div>
      {isActive && (
        <motion.span
          layoutId="navbar-active-link"
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
    </Link>
  );
}

// Refactored Nav Dropdown Menu
export function NavMenu() {
  const pathname = usePathname();
  const subdomainContext = getCurrentSubdomainContext();

  // Get contextual navigation based on current subdomain
  const contextualLinks = getContextualNavigationLinks();

  // For subdomains, show subdomain-specific navigation
  if (subdomainContext === 'notes' || subdomainContext === 'auth' || subdomainContext === 'learn') {
    const subdomainLinks = getSubdomainLinks(subdomainContext);

    return (
      <div className="flex items-center gap-1">
        {subdomainLinks.map((link) => (
          <AnimatedNavLink key={link.url} href={link.url}>
            <span>{link.name}</span>
          </AnimatedNavLink>
        ))}
      </div>
    );
  }

  // For main domain, show full navigation
  const visibleCategories = navigationCategories.slice(0, 6); // Show first 6 categories
  const overflowCategories = navigationCategories.slice(6);   // the rest

  const renderDropdown = (category: typeof navigationCategories[0]) => {
    const isCategoryActive = category.items.some(item => pathname === item.url);
    return (
      <DropdownMenu key={category.name}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "relative flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors duration-200 hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:bg-accent/40",
              isCategoryActive ? "text-primary bg-accent/30" : "text-foreground/70 hover:text-foreground"
            )}
          >
            <span>{category.name}</span>
            <ChevronDown className="h-3 w-3 transition-transform duration-200 group-data-[state=open]:rotate-180" />
            {isCategoryActive && (
              <motion.span
                layoutId="navbar-active-category"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {category.items.map((item) => (
            <DropdownMenuItem key={item.url} asChild>
              <Link
                href={item.url}
                className={cn(pathname === item.url && "font-semibold text-primary")}
              >
                {item.name}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      {/* These will be hidden on smaller screens via className */}
      <div className="hidden lg:flex items-center gap-1">
        {visibleCategories.map(renderDropdown)}
      </div>

      {/* Renders fewer items on medium screens */}
      <div className="hidden md:flex lg:hidden items-center gap-1">
        {visibleCategories.slice(0, 4).map(renderDropdown)}
      </div>

      {/* "More" dropdown for overflow items */}
      <DropdownMenu>
        {/* The trigger is hidden unless there are overflow items */}
        <DropdownMenuTrigger asChild className="hidden md:flex" aria-label="More navigation options">
          <Button
            variant="ghost"
            aria-label="More navigation options"
            className="flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors duration-200 hover:bg-accent/50 text-foreground/70 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:bg-accent/40"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden />
            <span className="sr-only xl:not-sr-only xl:inline">More</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {/* Combine remaining categories for different screen sizes */}
          <div className="lg:hidden">
            {visibleCategories.slice(4).map(cat => (
              <React.Fragment key={cat.name}>
                <DropdownMenuLabel>{cat.name}</DropdownMenuLabel>
                {cat.items.map(item => <DropdownMenuItem key={item.url} asChild><Link href={item.url}>{item.name}</Link></DropdownMenuItem>)}
              </React.Fragment>
            ))}
          </div>
          {overflowCategories.map((category) => (
            <React.Fragment key={category.name}>
              <DropdownMenuLabel>{category.name}</DropdownMenuLabel>
              {category.items.map((item) => (
                <DropdownMenuItem key={item.url} asChild>
                  <Link href={item.url}>{item.name}</Link>
                </DropdownMenuItem>
              ))}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

// User Auth section
export function UserNav({ handleSignOutAction }: { handleSignOutAction: () => void }) {
  const { user, loading, authKey } = useAuth();

  const { data: userData } = api.profile.getUserData.useQuery(undefined, { enabled: !!user });

  // Show loading state during auth transitions
  if (loading) {
    return (
      <div className="hidden md:flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled aria-label="Loading">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
        </Button>
      </div>
    );
  }

  if (user) {
    const avatarUrl = userData?.profile?.avatar_url || (user.prefs as any)?.avatar_url || null;
    const displayName =
      userData?.profile?.username ||
      userData?.profile?.display_name ||
      userData?.profile?.full_name ||
      user.name ||
      "User";
    const userInitials = (displayName?.charAt(0) || user.email?.charAt(0) || "U").toUpperCase();

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            aria-label="User menu"
            className="h-9 w-9 rounded-full hover:bg-accent/50 focus:bg-accent/40 focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-colors duration-200"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={avatarUrl || undefined} alt="User Avatar" />
              <AvatarFallback>
                {userInitials}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {displayName}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/dashboard"><span>Dashboard</span></Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/history"><span>History</span></Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/settings"><span>Settings</span></Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOutAction} className="text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/50">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Use /auth paths for consistency with footer (resolves to same auth flow)
  return (
    <div className="hidden md:flex items-center gap-2">
      <Button asChild variant="ghost" size="sm">
        <Link href="/auth/login" aria-label="Sign in to your account">Sign In</Link>
      </Button>
      <Button asChild size="sm">
        <Link href="/auth/signup" aria-label="Sign up for an account on this site">Sign Up</Link>
      </Button>
    </div>
  );
}

// Mobile Navigation with staggered animations
export function MobileNav({ isOpen, closeMenuAction, handleSignOutAction }: { isOpen: boolean, closeMenuAction: () => void, handleSignOutAction: () => void }) {
  const { user, loading, authKey } = useAuth();
  const pathname = usePathname();
  const subdomainContext = getCurrentSubdomainContext();

  const containerVariants: Variants = {
    open: {
      opacity: 1,
      height: 'auto',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        delayChildren: 0.05
      }
    },
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.02,
        staggerDirection: -1
      }
    }
  };

  const itemVariants: Variants = {
    open: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    closed: {
      opacity: 0,
      y: -10,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    }
  };

  const MobileNavLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
    <motion.div variants={itemVariants}>
      <Link
        href={href}
        onClick={closeMenuAction}
        className={cn(
          "flex items-center gap-3 rounded-lg p-3 text-sm font-medium transition-colors duration-200 hover:bg-accent/50 relative",
          pathname === href ? "text-primary bg-accent/40" : "text-muted-foreground"
        )}
      >
        {/* Active indicator */}
        {pathname === href && (
          <motion.div
            className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"
            layoutId="mobile-active-indicator"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
        )}
        <div className="relative z-10 flex items-center gap-3">
          {children}
        </div>
      </Link>
    </motion.div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="closed"
          animate="open"
          exit="closed"
          variants={containerVariants}
          className="md:hidden border-b border-border/50 bg-background/98 backdrop-blur-xl shadow-lg"
        >
          {/* Fixed height container with scrollable content */}
          <div className="relative max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
            <div className="flex flex-col space-y-1 p-4">
              {/* Main navigation items */}
              <MobileNavLink href="/"><Home className="h-5 w-5" /> Home</MobileNavLink>

              {/* Contextual navigation based on subdomain */}
              {subdomainContext === 'notes' || subdomainContext === 'auth' || subdomainContext === 'learn' ? (
                // Subdomain-specific navigation
                <motion.div variants={itemVariants} className="space-y-1">
                  <motion.h4
                    className="px-3 pt-3 pb-1 text-sm font-semibold text-foreground border-b border-border/50 relative overflow-hidden"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 25 }}
                  >
                    <motion.span
                      className="relative z-10"
                      whileHover={{ scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    >
                      {subdomainContext === 'notes'
                        ? 'Notes'
                        : subdomainContext === 'auth'
                          ? 'Authentication'
                          : 'Learn'}
                    </motion.span>
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/30 scale-x-0"
                      whileInView={{ scaleX: 1 }}
                      transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
                    />
                  </motion.h4>
                  <div className="space-y-1">
                    {getSubdomainLinks(subdomainContext).map((item, linkIndex) => (
                      <motion.div
                        key={item.url}
                        variants={itemVariants}
                        whileHover={{ scale: 1.01, x: 2 }}
                        whileTap={{ scale: 0.99 }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        <Link
                          href={item.url}
                          onClick={closeMenuAction}
                          className={cn(
                            "flex items-center gap-3 rounded-lg py-2.5 px-6 text-sm font-medium transition-colors duration-200 hover:bg-accent/50 relative",
                            pathname === item.url ? "text-primary bg-accent/40" : "text-muted-foreground"
                          )}
                        >
                          {/* Active indicator */}
                          {pathname === item.url && (
                            <motion.div
                              className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"
                              layoutId="mobile-active-indicator"
                              transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            />
                          )}
                          <span className="relative z-10">
                            {item.name}
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                // Full navigation for main domain
                navigationCategories.map((category, index) => (
                  <motion.div key={category.name} variants={itemVariants} className="space-y-1">
                    <motion.h4
                      className="px-3 pt-3 pb-1 text-sm font-semibold text-foreground border-b border-border/50 relative overflow-hidden"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.2 + index * 0.1,
                        type: "spring",
                        stiffness: 300,
                        damping: 25
                      }}
                    >
                      <motion.span
                        className="relative z-10"
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        {category.name}
                      </motion.span>
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/30 scale-x-0"
                        whileInView={{ scaleX: 1 }}
                        transition={{
                          delay: 0.5 + index * 0.1,
                          duration: 0.6,
                          ease: "easeOut"
                        }}
                      />
                    </motion.h4>
                    <div className="space-y-1">
                      {category.items.map((item, itemIndex) => (
                        <motion.div
                          key={item.url}
                          variants={itemVariants}
                          whileHover={{ scale: 1.01, x: 2 }}
                          whileTap={{ scale: 0.99 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        >
                          <Link
                            href={item.url}
                            onClick={closeMenuAction}
                            className={cn(
                              "flex items-center gap-3 rounded-lg py-2.5 px-6 text-sm font-medium transition-colors duration-200 hover:bg-accent/50 relative",
                              pathname === item.url ? "text-primary bg-accent/40" : "text-muted-foreground"
                            )}
                          >
                            {/* Active indicator */}
                            {pathname === item.url && (
                              <motion.div
                                className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"
                                layoutId="mobile-active-indicator"
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                              />
                            )}
                            <span className="relative z-10">
                              {item.name}
                            </span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))
              )}

              {/* Separator */}
              <motion.div variants={itemVariants}>
                <div className="pt-3 border-t border-border/50"></div>
              </motion.div>

              {/* User section */}
              {loading ? (
                <motion.div
                  variants={itemVariants}
                  className="flex items-center justify-center p-3"
                >
                  <motion.div
                    className="h-6 w-6 rounded-full border-2 border-primary/50 border-t-primary"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                </motion.div>
              ) : user ? (
                <>
                  <MobileNavLink href="/history"><User className="h-5 w-5" /> History</MobileNavLink>
                  <MobileNavLink href="/settings"><User className="h-5 w-5" /> Settings</MobileNavLink>
                  <motion.div variants={itemVariants}>
                    <button
                      onClick={() => { handleSignOutAction(); closeMenuAction(); }}
                      className="flex w-full items-center gap-3 rounded-lg p-3 text-sm font-medium transition-colors duration-200 hover:bg-red-50/10 dark:hover:bg-red-950/20 text-red-500 hover:text-red-600"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Sign Out</span>
                    </button>
                  </motion.div>
                </>
              ) : (
                <>
                  <MobileNavLink href="/auth/login"><User className="h-5 w-5" /> Sign In</MobileNavLink>
                  <MobileNavLink href="/auth/signup"><User className="h-5 w-5" /> Sign Up</MobileNavLink>
                </>
              )}

              {/* External links */}
              <MobileNavLink href="https://github.com/WilsonRIP"><Github className="h-5 w-5" /> GitHub</MobileNavLink>

              {/* Theme toggle */}
              <motion.div variants={itemVariants} className="flex items-center justify-between px-3 py-3 border-t border-border/50 mt-2">
                <span className="text-sm font-medium text-muted-foreground">Theme</span>
                <ThemeToggle />
              </motion.div>
            </div>
            {/* Subtle fade effect at bottom with enhanced glassmorphism */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-linear-to-t from-background/95 via-background/80 to-transparent pointer-events-none backdrop-blur-sm"></div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}